/* eslint-disable max-lines */
import { nativeEnum, z } from 'zod';
import {
  SCALED_SLIDER_MAX_VALUE,
  SCALED_SLIDER_MIN_VALUE,
  GENERAL_QUESTIONS_MAX_LENGTH,
  PRICES_SENSITIVITY_RANGE,
  RELATIVE_PRICES_OPTIONS,
  RELATIVE_PRICES_RANGE,
  TWO,
  VALIDATION_ERRORS,
  EMPTY_STRING,
  ATTRIBUTE_DESCRIPTION_MAX_LENGTH,
  NAME_LENGTH_RANGE,
  STEP,
  BUSINESS_COMPETITORS_RANGE,
  BLANK_COMPETITORS_RANGE,
  PASSWORD_MIN_LENGTH,
  PORT,
  INTERVIEW_TITLE,
  COMPETITOR_CONFIG_OPTIONS,
  PROJECT_SCOPE_RANGE,
  PROJECT_YEAR_MIN,
  TBUS_NAME_RANGE,
  ZERO,
  PROJECT_ROLES,
  SEGMENTATION_CODE_LENGTH,
  SEGMENTATION_NAME_LENGTH,
  SHORT_ID_RANGE,
  BUSINESS_NAME_RANGE,
  QUESTION_TYPES,
  QUESTION_OPTION_RANGE,
  PROJECT_QUESTION_RANGE,
  PROJECT_CATEGORY_RANGE,
  COMPANY_NAME_RANGE,
  INTERVIEWEE_NAME_RANGE,
  INTERVIEW_PRIORITY,
  ADDRESS_MAX_LENGTH,
  APPOINTMENT_DATE_FORMAT,
  EMAIL_TEXT_RANGE,
  AGGREGATION_NAME_RANGE,
  CHART_EXPORT_TYPE,
  IMPORT_INTERVIEWEE_ALLOWED_TYPES,
  COMPETITOR_STATE,
  PHONE_REGEX,
  CATEGORY_NAME_RANGE,
  HUNDRED,
} from '@constants';
import { isAttributeValid } from '@features/InterviewForm/Forms/Scores/helpers';
import { isStringEmpty } from '@utils/helpers';
import dayjs from 'dayjs';
import { store } from '@store/store';

import { ATTRIBUTE_TYPE, SUPPLLIER_TYPE } from './api';
import { checkCompetitorCount } from './helpers';

interface Range {
  MAX: number;
  MIN: number;
}

const setRequiredNameField = (range: Range, message: string) => {
  return z.string().nonempty({ message: 'CvaInput.Home104' }).min(range.MIN, message).max(range.MAX, message);
};

const phoneType = z.string().regex(PHONE_REGEX, { message: 'CvaInput.Home121' });

const SegmentationFieldSchema = z.object({
  type: z.number(),
  description: z.string(),
  selectedCode: z.string(),
  codes: z.record(z.string()),
});

export const SegmentationFormSchema = z.object({
  segments: z.array(SegmentationFieldSchema),
});

export type SegmentationField = z.infer<typeof SegmentationFieldSchema>;
export type SegmentationForm = z.infer<typeof SegmentationFormSchema>;

//INTERVIEW FROMS

const numberBetweenMinAndMax = z
  .number()
  .min(PRICES_SENSITIVITY_RANGE.MIN, { message: VALIDATION_ERRORS.PRICE_WEIGHT })
  .max(PRICES_SENSITIVITY_RANGE.MAX, { message: VALIDATION_ERRORS.PRICE_WEIGHT });

const RelativePricesFieldSchema = z
  .object({
    select: z.enum(RELATIVE_PRICES_OPTIONS as [string, ...string[]]),
    percentage: z
      .number()
      .min(RELATIVE_PRICES_RANGE.MIN, { message: VALIDATION_ERRORS.PRICE_RANGE })
      .max(RELATIVE_PRICES_RANGE.MAX, { message: VALIDATION_ERRORS.PRICE_RANGE })
      .optional(),
    supplierId: z.number(),
  })
  .refine((data) => data.select !== EMPTY_STRING, {
    message: VALIDATION_ERRORS.UNSET_PRICE_TYPE,
    path: ['select'],
  });

export const RelativePricesFormSchema = z.object({
  prices: z.array(RelativePricesFieldSchema),
});

const SuppliersFieldSchema = z.object({
  supplierId: z.number().optional(),
  name: z.string().optional(),
  supplierType: z.nativeEnum(SUPPLLIER_TYPE),
  relativePrice: z.number().optional(),
  seq: z.number(),
  shortName: z.string().nullable().optional(),
});

export const SuppliersFormSchema = z.object({
  suppliers: z
    .array(SuppliersFieldSchema)
    .min(TWO, {
      message: VALIDATION_ERRORS.SUPPLIERS_MIN,
    })
    .superRefine((suppliers, ctx) => {
      const filledNames = suppliers.filter((supplier) => !isStringEmpty(supplier.name));

      if (filledNames.length < TWO) {
        suppliers.forEach((supplier, index) => {
          if (!supplier.name || isStringEmpty(supplier.name)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: VALIDATION_ERRORS.SUPPLIER_NAME,
              path: [EMPTY_STRING, index, 'name'],
            });
          }
        });
      }

      const names = filledNames.map((supplier) => supplier.name);

      if (new Set(names).size !== names.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: VALIDATION_ERRORS.SUPPLIER_UNIQUE_NAME,
          path: [EMPTY_STRING],
        });
      }
    }),
});

const ImportanceFieldSchema = z
  .object({
    attributeId: z.number(),
    importance: z.union([z.number(), z.literal(EMPTY_STRING)]).optional(),
    included: z.boolean(),
    type: z.nativeEnum(ATTRIBUTE_TYPE),
  })
  .refine(
    ({ importance, included }) => {
      if (included) {
        if (String(importance) === EMPTY_STRING) {
          return false;
        }

        return (
          importance !== undefined && +importance >= SCALED_SLIDER_MIN_VALUE && +importance <= SCALED_SLIDER_MAX_VALUE
        );
      }

      return true;
    },
    {
      message: VALIDATION_ERRORS.IMPORTANCE_RANGE,
      path: ['importance'],
    },
  );

export const ImportanceFormSchema = z
  .object({
    attributes: z.array(ImportanceFieldSchema),
  })
  .refine((data) => data.attributes.some((attr) => attr.included));

export const PriceSensitivityFieldSchema = z.object({
  price: numberBetweenMinAndMax,
  quality: numberBetweenMinAndMax,
});

export const QuestionOptionsSchema = z.object({
  optionId: z.number(),
  optionText: z.string(),
});

export const GeneralQuestionsSchema = z.object({
  seq: z.number(),
  openQuestion: z.string().max(GENERAL_QUESTIONS_MAX_LENGTH, { message: 'CvaInput.Home160' }).optional(),
  numericalQuestion: z.number().nullable().optional(),
  closeQuestion: z.string().nullable().optional(),
  multipartQuestion: z.array(z.number()).optional(),
  options: z.array(QuestionOptionsSchema).optional(),
});

export const GeneralQuestionsFormSchema = z.object({
  questions: z.record(z.array(GeneralQuestionsSchema)),
});

const SupplierScoreSchema = z
  .object({
    supplier: z.string(),
    score: z
      .union([z.number(), z.literal(EMPTY_STRING)])
      .nullable()
      .optional(),
    included: z.boolean(),
  })
  .refine(
    ({ score, included }) => {
      if (included) {
        return (
          score !== undefined &&
          score !== null &&
          +score >= SCALED_SLIDER_MIN_VALUE &&
          +score <= SCALED_SLIDER_MAX_VALUE
        );
      }

      return true;
    },
    {
      message: VALIDATION_ERRORS.IMPORTANCE_RANGE,
      path: ['score'],
    },
  );

const ScoresFieldSchema = z
  .object({
    attributeId: z.number(),
    included: z.boolean(),
    importance: z.number().min(SCALED_SLIDER_MIN_VALUE).max(SCALED_SLIDER_MAX_VALUE).optional(),
    supplierScores: z.array(SupplierScoreSchema).optional(),
    note: z.union([z.string().max(350, { message: 'CvaInput.Home105' }), z.string().length(ZERO)]),
    name: z.string(),
    description: z.string().optional(),
    type: z.nativeEnum(ATTRIBUTE_TYPE),
  })
  .refine(({ included, supplierScores }) => {
    if (included) {
      return isAttributeValid(supplierScores);
    }

    return true;
  });

export const ScoresFormSchema = z.object({
  attributes: z.array(ScoresFieldSchema),
});

export const AttributePerformanceFormSchema = z.object({
  PRODUCT: z.array(ScoresFieldSchema),
  SERVICE: z.array(ScoresFieldSchema),
  IMAGE: z.array(ScoresFieldSchema),
});

export interface ReviewFormsState {
  SUPPLIERS: boolean;
  GENERAL_QUESTIONS: boolean;
  PRICE_SENSITIVITY: boolean;
  RELATIVE_PRICES: boolean;
  ATTRIBUTE_PERFORMANCE: boolean;
}

export interface FormStatePayload {
  key: keyof ReviewFormsState;
  value: boolean;
}

export type RelativePricesField = z.infer<typeof RelativePricesFieldSchema>;
export type RelativePricesForm = z.infer<typeof RelativePricesFormSchema>;

export type SuppliersField = z.infer<typeof SuppliersFieldSchema>;
export type SuppliersForm = z.infer<typeof SuppliersFormSchema>;

export type ImportanceField = z.infer<typeof ImportanceFieldSchema>;
export type ImportanceForm = z.infer<typeof ImportanceFormSchema>;

export type PriceSensitivityForm = z.infer<typeof PriceSensitivityFieldSchema>;
export type GeneralQuestionsForm = z.infer<typeof GeneralQuestionsFormSchema>;

export type ScoresField = z.infer<typeof ScoresFieldSchema>;
export type ScoresForm = z.infer<typeof ScoresFormSchema>;

export type SupplierScoreField = z.infer<typeof SupplierScoreSchema>;

export type AttributePerformanceForm = z.infer<typeof AttributePerformanceFormSchema>;

//PROJECT FORMS
export const ProjectCreationSchema = z.object({
  companyId: z.union([z.number(), z.string()]).refine((data) => Boolean(data)),
  projectName: z
    .string()
    .min(NAME_LENGTH_RANGE.MIN, { message: 'CvaInput.Home110' })
    .max(NAME_LENGTH_RANGE.MAX, { message: 'CvaInput.Home110' }),
  businessName: z
    .string()
    .min(NAME_LENGTH_RANGE.MIN, { message: 'CvaInput.Home110' })
    .max(NAME_LENGTH_RANGE.MAX, { message: 'CvaInput.Home110' }),
});

export const ProjectDuplicationSchema = ProjectCreationSchema.extend({
  sourceProjectId: z.union([z.number(), z.string()]).refine((data) => Boolean(data)),
  copyAttributes: z.boolean(),
  copyCompetitors: z.boolean(),
  copySegmentation: z.boolean(),
  copyQuestions: z.boolean(),
  copyConfiguration: z.boolean(),
}).refine(
  (data) =>
    data.copyAttributes ||
    data.copyCompetitors ||
    data.copySegmentation ||
    data.copyQuestions ||
    data.copyConfiguration,
  {
    message: 'CvaInput.Home112',
    path: ['copyAttributes'],
  },
);

export const ProjectAttributesFieldSchema = z.object({
  attributeSeq: z.number().optional(),
  attributeName: z
    .string()
    .nonempty({ message: 'CvaInput.Home106' })
    .min(NAME_LENGTH_RANGE.MIN, {
      message: (store.getState().translations.translations['CvaInput.Home118'] || EMPTY_STRING)
        .replace('{{ min }}', NAME_LENGTH_RANGE.MIN.toString())
        .replace('{{ max }}', NAME_LENGTH_RANGE.MAX.toString()),
    })
    .max(NAME_LENGTH_RANGE.MAX, {
      message: (store.getState().translations.translations['CvaInput.Home118'] || EMPTY_STRING)
        .replace('{{ min }}', NAME_LENGTH_RANGE.MIN.toString())
        .replace('{{ max }}', NAME_LENGTH_RANGE.MAX.toString()),
    }),
  attributeDescription: z.string().max(ATTRIBUTE_DESCRIPTION_MAX_LENGTH, { message: 'CvaInput.Home160' }).optional(),
});

export const CategoryNameSchema = z
  .string()
  .nonempty({ message: 'CvaInput.Home104' })
  .min(CATEGORY_NAME_RANGE.MIN, { message: 'CvaInput.Home163' })
  .max(CATEGORY_NAME_RANGE.MAX, { message: 'CvaInput.Home163' });

export const AttributeCategorySchema = z.object({
  categoryId: z.number(),
  categoryName: CategoryNameSchema,
  type: z.nativeEnum(ATTRIBUTE_TYPE),
  attributes: z.array(ProjectAttributesFieldSchema),
});

export const ProjectAttributesFormSchema = z
  .object({
    categories: z.array(AttributeCategorySchema),
  })
  .refine((data) => data.categories.flatMap((category) => category.attributes).length >= STEP, {
    message: 'CvaInput.Home107',
    path: ['categories'],
  })
  .superRefine((data, ctx) => {
    const allAttributes = data.categories.flatMap((category, categoryIndex) =>
      category.attributes.map((attr, attributeIndex) => ({
        ...attr,
        categoryIndex,
        attributeIndex,
        categoryName: category.categoryName,
      })),
    );

    const seen = new Map<string, { categoryIndex: number; attributeIndex: number }>();

    allAttributes.forEach(({ attributeName, categoryIndex, attributeIndex }) => {
      if (seen.has(attributeName)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'CvaInput.Home109',
          path: ['categories', categoryIndex, 'attributes', attributeIndex, 'attributeName'],
        });
      } else {
        seen.set(attributeName, { categoryIndex, attributeIndex });
      }
    });
  });

export const SurveyConfigurationSchema = z
  .object({
    titleConfig: z.nativeEnum(INTERVIEW_TITLE),
    introduction: z.string().optional(),
    instructions: z.string().optional(),
    competitorConfig: z.nativeEnum(COMPETITOR_CONFIG_OPTIONS),
    blankCompetitorCount: z.number().nullable().optional(),
    emailFooter: z.string().optional(),
    replyEmail: z.union([z.string().email('CvaInput.Home114'), z.string().length(ZERO)]),
    friendlyName: z.string().optional(),
    sendFrom: z.union([z.string().email('CvaInput.Home114'), z.string().length(ZERO)]),
    friendlyName2: z.string().optional(),
    password: z.union([z.string().min(PASSWORD_MIN_LENGTH, 'CvaInput.Home113'), z.string().length(ZERO)]),
    emailServer: z.string().optional(),
    emailPort: z.nativeEnum(PORT).refine((value) => String(value) !== EMPTY_STRING, {
      message: 'CvaInput.PriceErr1',
    }),
    selectedCompetitors: z.array(z.number()),
  })
  .refine(
    (data) => {
      return checkCompetitorCount({
        type: COMPETITOR_CONFIG_OPTIONS.blank,
        competitorConfig: data.competitorConfig,
        competitorCount: data.blankCompetitorCount,
        range: BLANK_COMPETITORS_RANGE,
      });
    },
    {
      message: (store.getState().translations.translations['CvaInput.Home115'] || EMPTY_STRING).replace(
        '{{ count }}',
        BLANK_COMPETITORS_RANGE.MIN.toString(),
      ),
      path: ['blankCompetitorCount'],
    },
  )
  .refine(
    (data) =>
      checkCompetitorCount({
        type: COMPETITOR_CONFIG_OPTIONS.businessBlanks,
        competitorConfig: data.competitorConfig,
        competitorCount: data.blankCompetitorCount,
        range: BUSINESS_COMPETITORS_RANGE,
      }),
    {
      message: (store.getState().translations.translations['CvaInput.Home115'] || EMPTY_STRING).replace(
        '{{ count }}',
        BUSINESS_COMPETITORS_RANGE.MIN.toString(),
      ),
      path: ['blankCompetitorCount'],
    },
  )
  .refine(
    (data) => {
      if (data.competitorConfig === COMPETITOR_CONFIG_OPTIONS.selected) {
        return data.selectedCompetitors && data.selectedCompetitors.length >= BLANK_COMPETITORS_RANGE.MIN;
      }

      return true;
    },
    {
      message: 'CvaInput.Home131',
      path: ['selectedCompetitors'],
    },
  );

export const ProjectDetailsFormSchema = z.object({
  surveyName: z
    .string()
    .nonempty({ message: 'CvaProj.Setup3' })
    .min(NAME_LENGTH_RANGE.MIN, { message: 'CvaInput.Home108' })
    .max(NAME_LENGTH_RANGE.MAX, { message: 'CvaInput.Home108' }),
  displayName: z
    .string()
    .nonempty({ message: 'CvaInput.Home117' })
    .min(NAME_LENGTH_RANGE.MIN, { message: 'CvaInput.Home108' })
    .max(NAME_LENGTH_RANGE.MAX, { message: 'CvaInput.Home108' }),
  surveyScope: z
    .union([
      z
        .string()
        .min(PROJECT_SCOPE_RANGE.MIN, {
          message: (store.getState().translations.translations['CvaInput.Home118'] || EMPTY_STRING).replace(
            '{{ min }}',
            PROJECT_SCOPE_RANGE.MIN.toString(),
          ),
        })
        .max(PROJECT_SCOPE_RANGE.MAX, { message: 'CvaInput.Home160' }),
      z.string().length(ZERO),
    ])
    .optional(),
  defaultLangCode: z.string().nonempty({ message: 'CvaInput.PriceErr1' }),
  year: z.number().int().min(PROJECT_YEAR_MIN, 'CvaInput.Home119').max(dayjs().year(), 'CvaInput.Home119'),
  projectManagerUserId: z.string(),
  businessName: setRequiredNameField(
    TBUS_NAME_RANGE,
    (store.getState().translations.translations['CvaInput.Home118'] || EMPTY_STRING)
      .replace('{{ min }}', TBUS_NAME_RANGE.MIN.toString())
      .replace('{{ max }}', TBUS_NAME_RANGE.MAX.toString()),
  ),
});

export const StaffCreationFormSchema = z.object({
  email: z.string().email({ message: 'CvaInput.Home114' }),
  phone: z.union([phoneType, z.string().length(ZERO)]),
  firstName: z.string().min(STEP, { message: 'CvaInput.Home104' }),
  lastName: z.string().min(STEP, { message: 'CvaInput.Home104' }),
  userId: z.number().optional(),
  projectRole: z.nativeEnum(PROJECT_ROLES).nullable(),
  countryCode: z.string().optional(),
  fluentLanguages: z.array(z.object({ code: z.string() })).optional(),
  isActive: z.boolean().nullable().optional(),
});

export const SetupSegmentationCodesSchema = z.array(
  z.object({
    key: z
      .string()
      .min(SEGMENTATION_CODE_LENGTH.MIN, { message: 'CvaInput.Home104' })
      .max(SEGMENTATION_CODE_LENGTH.MAX, { message: 'CvaInput.Home130' })
      .regex(/^[0-9]+$/, { message: 'CvaInput.Home129' }),
    value: z
      .string()
      .min(SEGMENTATION_NAME_LENGTH.MIN, { message: 'CvaInput.Home126' })
      .max(SEGMENTATION_NAME_LENGTH.MAX, { message: 'CvaInput.Home127' }),
  }),
);

export const SetupSegmentationSchema = z
  .object({
    type: z.number().optional(),
    headerCode: z.number().optional(),
    description: z
      .string()
      .min(SEGMENTATION_NAME_LENGTH.MIN, { message: 'CvaInput.Home127' })
      .max(SEGMENTATION_NAME_LENGTH.MAX, { message: 'CvaInput.Home127' }),
    codes: SetupSegmentationCodesSchema,
  })
  .superRefine((data, ctx) => {
    const allItems = data.codes.map((code, index) => ({ ...code, index }));
    const seenKeys = new Map<string, number>();
    const seenValues = new Map<string, number>();

    allItems.forEach(({ key, value, index }) => {
      const lowerKey = key.toLowerCase();
      const lowerValue = value.toLowerCase();

      if (seenKeys.has(lowerKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'CvaInput.Home123',
          path: ['codes', index, 'key'],
        });
      } else {
        seenKeys.set(lowerKey, index);
      }

      if (seenValues.has(lowerValue)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'CvaInput.Home124',
          path: ['codes', index, 'value'],
        });
      } else {
        seenValues.set(lowerValue, index);
      }
    });
  });

export const CompetitorFieldSchema = z.object({
  shortId: setRequiredNameField(
    SHORT_ID_RANGE,
    (store.getState().translations.translations['CvaInput.Home118'] || EMPTY_STRING)
      .replace('{{ min }}', SHORT_ID_RANGE.MIN.toString())
      .replace('{{ max }}', SHORT_ID_RANGE.MAX.toString()),
  ),
  businessName: setRequiredNameField(
    BUSINESS_NAME_RANGE,
    (store.getState().translations.translations['CvaInput.Home118'] || EMPTY_STRING)
      .replace('{{ min }}', BUSINESS_NAME_RANGE.MIN.toString())
      .replace('{{ max }}', BUSINESS_NAME_RANGE.MAX.toString()),
  ),
  competitorId: z.number().optional(),
  state: nativeEnum(COMPETITOR_STATE).default(COMPETITOR_STATE.DEFAULT),
  assignedCompetitorId: z.number().optional(),
  marketShare: z
    .number()
    .min(ZERO, store.getState().translations.translations['CvaProj.Setup204'] || EMPTY_STRING)
    .max(HUNDRED, store.getState().translations.translations['CvaProj.Setup204'] || EMPTY_STRING)
    .nullable(),
});

export const CompetitorsFormSchema = z
  .object({
    thisBusiness: CompetitorFieldSchema,
    competitors: z.array(CompetitorFieldSchema),
  })
  .superRefine((data, ctx) => {
    const shortIdSet = new Set([data.thisBusiness.shortId]);
    const businessNameSet = new Set([data.thisBusiness.businessName]);

    data.competitors.forEach((competitor, index) => {
      if (shortIdSet.has(competitor.shortId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'CvaInput.Home132',
          path: ['competitors', index, 'shortId'],
        });
      } else {
        shortIdSet.add(competitor.shortId);
      }

      if (businessNameSet.has(competitor.businessName)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'CvaInput.HomeA',
          path: ['competitors', index, 'businessName'],
        });
      } else {
        businessNameSet.add(competitor.businessName);
      }
    });
  });

export const SetupSegmentationFormSchema = z
  .object({
    segments: z.array(SetupSegmentationSchema),
  })
  .superRefine((data, ctx) => {
    const seenCategories = new Set<string>();

    for (let index = data.segments.length - STEP; index >= ZERO; index--) {
      const segment = data.segments[index];
      const lowerCategory = segment.description.toLowerCase();

      if (seenCategories.has(lowerCategory)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'CvaInput.Home122',
          path: ['segments', index, 'description'],
        });
      } else {
        seenCategories.add(lowerCategory);
      }
    }
  });

const OptionSchema = z.object({
  optionText: z.string().max(QUESTION_OPTION_RANGE.MAX, {
    message: (store.getState().translations.translations['CvaInput.Home118'] || EMPTY_STRING)
      .replace('{{ min }}', QUESTION_OPTION_RANGE.MIN.toString())
      .replace('{{ max }}', QUESTION_OPTION_RANGE.MAX.toString()),
  }),
});

const QuestionSchema = z
  .object({
    question: setRequiredNameField(PROJECT_QUESTION_RANGE, 'CvaInput.Home133'),
    questionId: z.number().optional(),
    type: z.string().default(EMPTY_STRING),
    options: z.array(OptionSchema).max(PROJECT_CATEGORY_RANGE.MAX, 'CvaInput.Home138'),
  })
  .superRefine((data, ctx) => {
    if (!Object.values(QUESTION_TYPES).includes(data.type as QUESTION_TYPES)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'CvaInput.PriceErr1',
        path: ['type'],
      });
    }

    if (data.type === (QUESTION_TYPES.MULTIPART as string)) {
      const filledOptions = data.options.filter((option) => !isStringEmpty(option.optionText));
      if (filledOptions.length < TWO) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'CvaInput.Home137',
          path: ['options'],
        });
      }
      const uniqueOptions = new Set(filledOptions.map((option) => option.optionText));
      if (uniqueOptions.size !== filledOptions.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'CvaInput.Home139',
          path: ['options'],
        });
      }
    }
  });

const CategorySchema = z.object({
  category: setRequiredNameField(PROJECT_CATEGORY_RANGE, 'CvaInput.Home135'),
  categoryId: z.number().optional(),
  questions: z.array(QuestionSchema).min(STEP, 'CvaInput.Home136'),
});

export const ProjectGeneralQuestionsFormSchema = z.object({
  categories: z.array(CategorySchema).superRefine((categories, ctx) => {
    const categoryNameSet = new Set();
    for (let index = categories.length - STEP; index >= ZERO; index--) {
      const category = categories[index];
      if (categoryNameSet.has(category.category)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'CvaInput.Home122',
          path: [EMPTY_STRING, index, 'category'],
        });
      } else {
        categoryNameSet.add(category.category);
      }
    }
  }),
});

export const CompanyCreationFormSchema = z.object({
  companyName: setRequiredNameField(COMPANY_NAME_RANGE, 'CvaInput.Home141'),
});

export const SegmentationSchema = z.object({
  type: z.number(),
  description: z.string(),
  codes: z.record(z.string()),
  selectedCode: z.union([z.string().length(ZERO), z.number()]),
});

const InterviewDetailsFormSchema = z.object({
  businessId: z.number(),
  worksheetId: z.number(),
  interviewType: z.string(),
  company: z.string().max(INTERVIEWEE_NAME_RANGE.MAX, 'CvaInput.Home160'),
  interviewee: setRequiredNameField(
    INTERVIEWEE_NAME_RANGE,
    (store.getState().translations.translations['CvaInput.Home118'] || EMPTY_STRING)
      .replace('{{ min }}', INTERVIEWEE_NAME_RANGE.MIN.toString())
      .replace('{{ max }}', INTERVIEWEE_NAME_RANGE.MAX.toString()),
  ),
  address: z.string().max(ADDRESS_MAX_LENGTH, 'CvaInput.Home105'),
  phone: phoneType,
  langCode: z.string().nonempty({ message: 'CvaInput.PriceErr1' }),
  interviewerId: z.union([z.string().nonempty('CvaInput.PriceErr1'), z.number()]),
  email: z.string().nonempty({ message: 'CvaInput.Home104' }).email({ message: 'CvaInput.Home114' }),
  jobTitle: z.string().max(INTERVIEWEE_NAME_RANGE.MAX, 'CvaInput.Home160'),
  postcode: z
    .string()
    .max(10, 'CvaInput.Home160')
    .regex(/^[a-zA-Z0-9\s-]*$/, { message: 'CvaInput.Home128' }),
  mobileNumber: phoneType,
  salesContact: z.string().max(COMPANY_NAME_RANGE.MAX, 'CvaInput.Home160'),
  segments: z.array(SegmentationSchema),
  priority: z.nativeEnum(INTERVIEW_PRIORITY),
  jointInterviewId: z.union([z.string().length(ZERO), z.number()]),
});

const AppointmentDateSchemaWithoutCheck = z.object({
  appointmentDate: z.string(),
});

const AppointmentDateSchemaWithCheck = z.object({
  appointmentDate: z.string().superRefine((data, ctx) => {
    if (!data) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: EMPTY_STRING,
        path: [EMPTY_STRING],
      });
    }

    const date = dayjs(data, APPOINTMENT_DATE_FORMAT, true);

    if (!(date.isValid() && date.isAfter(dayjs(), 'minute'))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'CvaInput.Home143',
        path: [EMPTY_STRING],
      });
    }
  }),
});

export const InterviewConfirmationFormSchema = z.object({
  from: z.string().email({ message: 'CvaInput.Home114' }),
  to: z.string().email({ message: 'CvaInput.Home114' }),
  cc: z.union([z.string().email({ message: 'CvaInput.Home114' }), z.string().length(ZERO)]),
  subject: z.string().max(INTERVIEWEE_NAME_RANGE.MAX, 'CvaInput.Home160'),
  emailText: setRequiredNameField(EMAIL_TEXT_RANGE, 'CvaInput.Home144'),
});

export const InterviewDetailsFormSchemaWithoutDateCheck = InterviewDetailsFormSchema.merge(
  AppointmentDateSchemaWithoutCheck,
);
export const InterviewDetailsFormSchemaWithDateCheck = InterviewDetailsFormSchema.merge(AppointmentDateSchemaWithCheck);

export type ProjectAttributesForm = z.infer<typeof ProjectAttributesFormSchema>;

export type ProjectCreationForm = z.infer<typeof ProjectCreationSchema | typeof ProjectDuplicationSchema>;
export type ProjectDuplicationFields = z.infer<typeof ProjectDuplicationSchema>;
export type SurveyConfigurationForm = z.infer<typeof SurveyConfigurationSchema>;
export type ProjectDetailsForm = z.infer<typeof ProjectDetailsFormSchema>;
export type StaffCreationForm = z.infer<typeof StaffCreationFormSchema>;
export type SetupSegmentationForm = z.infer<typeof SetupSegmentationFormSchema>;

export type CompetitorField = z.infer<typeof CompetitorFieldSchema>;
export type CompetitorsForm = z.infer<typeof CompetitorsFormSchema>;

export type ProjectGeneralQuestionsForm = z.infer<typeof ProjectGeneralQuestionsFormSchema>;
export type GeneralQuestionCategory = z.infer<typeof CategorySchema>;
export type GeneralQuestionField = z.infer<typeof QuestionSchema>;
export type CompanyCreationForm = z.infer<typeof CompanyCreationFormSchema>;

export type InterviewDetailsFormType = z.infer<typeof InterviewDetailsFormSchemaWithDateCheck>;

export type InterviewConfirmationFormType = z.infer<typeof InterviewConfirmationFormSchema>;

//ANALYSIS
export const AddCategorySchema = z.object({
  name: z.union([setRequiredNameField(PROJECT_CATEGORY_RANGE, 'CvaInput.Home135'), z.string().length(ZERO)]),
  aggregation: z.union([z.number(), z.string().length(ZERO)]),
});

export const DeleteCategorySchema = z.object({
  deleteCategories: z
    .array(z.object({ categoryId: z.number(), categoryName: z.string(), toDelete: z.boolean() }))
    .refine((data) => data.some((el) => el.toDelete)),
});

export const EditCategorySchema = z.object({
  editCategories: z
    .array(
      z.object({
        categoryId: z.number(),
        originalName: z.string(),
        newName: setRequiredNameField(PROJECT_CATEGORY_RANGE, 'CvaInput.Home135'),
      }),
    )
    .refine((data) => data.some((el) => el.newName !== el.originalName)),
});

const aggregatedSegmentsSchema = z.array(
  z.object({
    type: z.number(),
    segmentCodes: z.array(z.number()),
  }),
);

export const AddEditAggregationSchema = z.object({
  name: z
    .string()
    .min(AGGREGATION_NAME_RANGE.MIN, { message: 'CvaInput.Home145' })
    .max(AGGREGATION_NAME_RANGE.MAX, { message: 'CvaInput.Home145' }),
  year: z.number(),
  categoryId: z.string({
    required_error: 'CvaInput.Home147',
  }),
  worksheetIds: z.array(z.number()),
  aggregatedSegments: aggregatedSegmentsSchema,
});

export const DeleteAggregationsSchema = z.object({
  deleteAggregations: z
    .array(
      z.object({
        aggregationId: z.number(),
        name: z.string(),
        toDelete: z.boolean(),
      }),
    )
    .refine((data) => data.some((el) => el.toDelete)),
});

export const getAttributeSettingsSchema = (ratingRange: Range, importanceRange: Range) => {
  const ratingRangeMessage = (store.getState().translations.translations['CvaInput.Home149'] || EMPTY_STRING)
    .replace('{{min}}', ratingRange.MIN.toString())
    .replace('{{max}}', ratingRange.MAX.toString());
  const maxImportanceMessage = (store.getState().translations.translations['CvaInput.Home149'] || EMPTY_STRING)
    .replace('{{min}}', importanceRange.MIN.toString())
    .replace('{{max}}', importanceRange.MAX.toString());

  return z
    .object({
      minRating: z
        .number()
        .min(ratingRange.MIN, { message: ratingRangeMessage })
        .max(ratingRange.MAX, { message: ratingRangeMessage }),

      maxRating: z
        .number()
        .min(ratingRange.MIN, { message: ratingRangeMessage })
        .max(ratingRange.MAX, { message: ratingRangeMessage }),

      minImportance: z
        .number()
        .min(importanceRange.MIN, { message: maxImportanceMessage })
        .max(importanceRange.MAX, { message: maxImportanceMessage }),

      maxImportance: z
        .number()
        .min(importanceRange.MIN, { message: maxImportanceMessage })
        .max(importanceRange.MAX, { message: maxImportanceMessage }),

      hideNames: z.boolean().optional(),
    })
    .refine(
      (data) => {
        const { minRating, maxRating } = data;

        return maxRating >= minRating;
      },
      {
        message: ratingRangeMessage,
        path: ['maxRating'],
      },
    )
    .refine(
      (data) => {
        const { minImportance, maxImportance } = data;

        return maxImportance >= minImportance;
      },
      {
        message: maxImportanceMessage,
        path: ['maxImportance'],
      },
    );
};

export const ValueMapSettingsSchema = z.object({
  xrange: z.number().optional(),
  yrange: z.number().optional(),
  bubbleSize: z.number().optional(),
  hideNames: z.boolean().optional(),
  intervalX: z.number().optional(),
  intervalY: z.number().optional(),
});

export type AttributeSettingsForm = {
  minRating: number;
  maxRating: number;
  minImportance: number;
  maxImportance: number;
  hideNames: boolean;
};

export type AddCategoryForm = z.infer<typeof AddCategorySchema>;
export type DeleteCategoryForm = z.infer<typeof DeleteCategorySchema>;
export type EditCategoryForm = z.infer<typeof EditCategorySchema>;
export type AddEditAggregationForm = z.infer<typeof AddEditAggregationSchema>;
export type DeleteAggregationsForm = z.infer<typeof DeleteAggregationsSchema>;

export const ChartExportFormSchema = z.object({
  type: z.nativeEnum(CHART_EXPORT_TYPE),
  usePresetSizes: z.boolean(),
  width: z.number(),
  height: z.number(),
  percentWidth: z.number(),
  percentHeight: z.number(),
});

export const ImportIntervieweeSchema = z.object({
  file: z
    .instanceof(File)
    .nullable()
    .refine((file) => file === null || IMPORT_INTERVIEWEE_ALLOWED_TYPES.includes(file.type), {
      message: 'CvaInput.Home',
    }),
});

export type ImportIntervieweeForm = z.infer<typeof ImportIntervieweeSchema>;
export type ChartExportForm = z.infer<typeof ChartExportFormSchema>;
